/* import __COLOCATED_TEMPLATE__ from './fin-with-workflows.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import type ContentWrapper from 'embercom/models/content-service/content-wrapper';
import type AiAgentSetupService from 'embercom/services/ai-agent-setup-service';
import { Column, ColumnConfigs } from 'embercom/objects/operator/configuration/custom-bot';
import type IntlService from 'embercom/services/intl';
import { statisticKeys } from 'embercom/models/data/outbound/constants';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type RouterService from '@ember/routing/router-service';
import type Store from '@ember-data/store';
import { states } from 'embercom/models/data/matching-system/matching-constants';

interface Signature {
  Element: HTMLDivElement;
  Args: {
    workflows: ContentWrapper[];
    pageSize: number;
    isLoading: boolean;
  };
}

enum SortDirection {
  ASC = 'asc',
  DESC = 'desc',
}

type TableSortState = {
  valuePath: string;
  direction: SortDirection;
};

export default class FinWithWorkflows extends Component<Signature> {
  @service declare aiAgentSetupService: AiAgentSetupService;
  @service declare intl: IntlService;
  @service declare router: RouterService;
  @service declare store: Store;

  @tracked sortBy: TableSortState = { valuePath: 'state', direction: SortDirection.ASC };

  readonly columnConfigs = new ColumnConfigs();
  statisticColumns = [{ valuePath: 'sentCount', key: statisticKeys.receipts }];

  get hasFinWorkflows() {
    let hasFinChatWorkflows =
      this.aiAgentSetupService.channelType === 'chat' &&
      this.aiAgentSetupService.finStats.workflowsCount.hasFinChatWorkflows;
    let hasFinEmailWorkflows =
      this.aiAgentSetupService.channelType === 'email' &&
      this.aiAgentSetupService.finStats.workflowsCount.hasFinEmailWorkflows;

    return hasFinChatWorkflows || hasFinEmailWorkflows;
  }

  get columnKeys(): (keyof ColumnConfigs)[] {
    return ['title', 'state', 'channels', 'lastUpdatedBy', 'sentCount'];
  }

  get columns() {
    return this.columnKeys.map((key) => {
      let col = new Column(this.intl, this.columnConfigs[key]);
      if (key === 'state') {
        col.isSortable = true;
      }
      return col;
    });
  }

  get sortDirection() {
    return this.sortBy.direction;
  }

  stateSortPriority: Record<number, number> = {
    [states.live]: 0,
    [states.paused]: 1,
    [states.draft]: 2,
  };

  get sortedData() {
    let key = this.sortBy.valuePath;

    return [...this.args.workflows].sort((a: any, b: any) => {
      let aVal = a[key] || a.contents?.firstObject?.[key];
      let bVal = b[key] || b.contents?.firstObject?.[key];

      // Custom ordering for state column: live → paused → draft (ASC)
      if (key === 'state') {
        let aPriority = this.stateSortPriority[aVal] ?? 99;
        let bPriority = this.stateSortPriority[bVal] ?? 99;

        let comparison = aPriority - bPriority;
        return this.sortDirection === SortDirection.ASC ? comparison : -comparison;
      }

      if (aVal > bVal) {
        return this.sortDirection === SortDirection.ASC ? -1 : 1;
      } else if (aVal < bVal) {
        return this.sortDirection === SortDirection.ASC ? 1 : -1;
      }

      return 0;
    });
  }

  @action
  onSortUpdate(sortBy: string) {
    let sort: TableSortState = {
      valuePath: sortBy,
      direction:
        this.sortBy.direction === SortDirection.ASC ? SortDirection.DESC : SortDirection.ASC,
    };
    this.sortBy = sort;
  }

  @action
  async openWorkflow(rulesetId: string) {
    this.router.transitionTo('apps.app.automation.workflows.edit', rulesetId, {
      queryParams: {
        mode: 'edit',
        visual: 'enabled',
        returnPath: this.router.currentRouteName,
      },
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Operator::Fin::Setup::FinWithWorkflows': typeof FinWithWorkflows;
    'operator/fin/setup/fin-with-workflows': typeof FinWithWorkflows;
  }
}
